import type {
  ChangeRequest,
  DocSection,
  RoleSave,
  JobDescription,
  Me,
  Page,
  ProposableField,
  ChangeValue,
  SaveStatus,
  UploadJob,
  VersionEntry,
  MarketPage,
  MarketRecord,
  MarketSurvey,
  SurveyColumn,
  SurveyFilter,
  ColumnValues,
  FilterClause,
  AdminCompanySummary,
  AdminCompanyDetail,
  AdminPerson,
  GiveableRole,
  NewLogin,
  Benchmark,
  FeedbackView,
  IconicChat,
  IconicChatView,
  IconicTurn,
  BenchmarkOverview,
  RunProgress,
  RunState,
  MatchChat,
  MatchTurn,
  MatchProgress,
  MatchState,
  SalaryRanges,
  PublishedSet,
  RangeDetail,
  Pct,
} from '../types'
import { sendWithToken, tokenSource } from './tokens'
import { ApiError } from '../types'
import { putWithRetry } from '../lib/upload'
import { signInWithSrp } from '../auth/srp'
import type { Challenge } from '../auth/srp'

/**
 * The shape every screen talks to. Two implementations exist: `httpApi`, which
 * calls the routes in STE-536 Part 4, and the mock in ./mock, which serves the
 * same contract from the browser while the backend (STE-540) is being built.
 */
/**
 * What a sign in ended in.
 *
 * Somebody on a temporary password does not get a session yet, and that is not
 * a failure. They get the screen that asks them to choose their own password,
 * and finishing it signs them straight in.
 */
export type LoginOutcome =
  | { kind: 'signedIn'; me: Me }
  | { kind: 'newPasswordNeeded'; challenge: Challenge }

export interface Api {
  login(email: string, password: string): Promise<LoginOutcome>
  /** Sets the password somebody chose on their first sign in, and signs them in. */
  finishNewPassword(challenge: Challenge, password: string): Promise<Me>
  me(): Promise<Me>
  logout(): Promise<void>

  listJobDescriptions(params: {
    q?: string
    cursor?: string | null
    /** A page by number, for the screen. A full read uses `cursor`. */
    page?: number
    limit?: number
  }): Promise<Page<JobDescription>>
  getJobDescription(id: string): Promise<JobDescription>
  getVersions(id: string): Promise<VersionEntry[]>
  /** A draft from an upload carries the document's sections into the role. */
  createJobDescription(body: RoleSave, status: SaveStatus): Promise<JobDescription>
  updateJobDescription(id: string, body: RoleSave, status: SaveStatus): Promise<JobDescription>
  /** Removes the role and its whole version history. There is no way back. */
  deleteJobDescription(id: string): Promise<void>
  exportCsv(q: string): Promise<{ fileName: string; body: string }>
  exportDocument(id: string): Promise<{ fileName: string; body: string }>

  /** Single-document upload. Returns a job to poll; extraction is not instant. */
  startExtraction(file: File | null, text: string): Promise<UploadJob>
  /**
   * A spreadsheet of roles. `sheet` is the Excel file itself when that is what
   * was chosen: it is bytes, so it is sent as it is for the server to read,
   * where a `.csv` is text and is sent as text.
   */
  startBulkCheck(text: string, fileName: string, sheet?: File | null): Promise<UploadJob>
  /**
   * One document holding many roles. Claude reads it into the same rows a
   * spreadsheet produces, so the preview and the import are shared.
   */
  startDocsetCheck(file: File): Promise<UploadJob>
  getUpload(id: string): Promise<UploadJob>
  /**
   * Imports what it can and says whether there is more.
   *
   * A big file cannot import inside one request, so this is called until it
   * reports done. See `commitAll`.
   */
  commitUpload(id: string): Promise<{
    imported: number
    done?: boolean
    position?: number
    total?: number
    alreadyImported?: boolean
  }>
  /**
   * Fills in a job family or a level on rows that arrived without one, and
   * checks them again. A document almost never states either, so without this
   * a real customer's file is a preview of work they cannot finish.
   */
  setOnRows(id: string, patch: { lines?: number[]; family?: string; level?: string }): Promise<UploadJob>
  /**
   * Changes the key details of one previewed row, and its sections when they
   * are sent, and re-checks it (FR-59).
   */
  editRow(id: string, line: number, set: Record<string, string>, sections?: DocSection[]): Promise<UploadJob>

  // -------------------------------------------------------------------------
  // Market data. Admin only: every route below refuses a manager's token, and
  // the navigation item does not exist for them either.
  // -------------------------------------------------------------------------

  /**
   * Records, searched and filtered.
   *
   * `filters` is keyed by column key, from the chosen survey's own filters,
   * and holds the values that column is allowed to have. Nothing here knows a
   * column name in advance, which is the whole shape of the module.
   */
  listMarketData(params: {
    q?: string
    survey?: string
    /** What each narrowed column requires. Every clause has to pass. */
    clauses?: FilterClause[]
    cursor?: string | null
    limit?: number
    /** Start at this line of the survey, so a page can be reached directly. */
    from?: number
    /** Cancels the request when a later search replaces this one. */
    signal?: AbortSignal
  }): Promise<MarketPage>
  /** One column's values, for its filter to list. */
  getColumnValues(surveyId: string, columnKey: string): Promise<ColumnValues>
  getMarketRecord(id: string): Promise<{ record: MarketRecord; survey: MarketSurvey }>
  updateMarketRecord(id: string, values: Record<string, string>): Promise<MarketRecord>
  deleteMarketRecord(id: string): Promise<{ surveyRemoved: boolean }>

  /* ---------- benchmarking ---------- */

  /**
   * The four counts and every role with where it stands.
   *
   * `hasSurvey` is false for a company that has bought no market data yet.
   * That is not an error: they are at the beginning, and the screen tells them
   * what to do first.
   */
  benchmarkOverview(params?: {
    q?: string
    /** Counting from zero. */
    page?: number
    limit?: number
  }): Promise<BenchmarkOverview>

  /** How many and roughly how long, before anything runs. Never a cost. */
  benchmarkEstimate(scope: 'changed' | 'all'): Promise<{ scope: string; count: number; seconds: number }>

  /** Starts a run. Answers at once; a worker does the rest. */
  startBenchmarkRun(scope: 'changed' | 'all'): Promise<{ runId: string; count: number; alreadyRunning?: boolean }>

  /**
   * Starts a run for one role, whether or not it is already current.
   *
   * A person pressing Benchmark on a role wants it looked at, and answering
   * "it is already up to date" refuses the thing they asked for.
   */
  benchmarkOneRole(jdId: string): Promise<{ runId: string; count: number; alreadyRunning?: boolean }>

  benchmarkRun(runId: string): Promise<RunProgress>

  /** Asks a run to stop. It finishes the role it is on rather than stopping mid-write. */
  stopBenchmarkRun(runId: string): Promise<{ state: RunState }>

  getBenchmark(jdId: string): Promise<Benchmark>

  /**
   * HR disagrees with a role's match and says why (STE-991). The role is
   * matched again in the background; the new answer waits beside the
   * benchmark until HR keeps one. `replaceConfirmed` says HR was told a match
   * picked by hand may be replaced and went ahead.
   */
  askFeedback(jdId: string, ask: { reason: string; surveyJob?: string; replaceConfirmed?: boolean }): Promise<{ feedbackId: string }>
  /** The waiting answer, marked stale if the role or its survey moved, and the record. */
  getFeedback(jdId: string): Promise<FeedbackView>
  /** Keep the new match: it becomes the benchmark. */
  keepFeedback(jdId: string, feedbackId: string): Promise<Benchmark>
  /** Keep the current one: the benchmark is not touched. */
  discardFeedback(jdId: string, feedbackId: string): Promise<{ kept: 'current' }>

  /* ---------- Match a job ---------- */

  /** Whether this company has any market data to match against. */
  matchOverview(): Promise<{ hasSurvey: boolean }>

  /** Starts a conversation. Nothing has been sent yet. */
  startMatchChat(): Promise<{ chatId: string; turns: MatchTurn[] }>

  /** The whole conversation, for picking it up again and for the download. */
  matchChat(chatId: string): Promise<MatchChat>

  /** Forgets a conversation, and the text that was pasted into it. */
  forgetMatchChat(chatId: string): Promise<void>

  /**
   * Sends a job description, a title, or a follow-up.
   *
   * Comes back at once with an id to check on. A match is about twelve trips
   * to the AI and a request is cut off at 30 seconds, so the answer is
   * collected by `matchProgress` rather than waited for here.
   *
   * `alreadyGoing` means a match in this chat has not finished. Starting a
   * second would not be faster, it would make both slower.
   */
  sendToMatch(chatId: string, text: string): Promise<{
    matchId: string
    chatId?: string
    alreadyGoing?: boolean
    message?: string
  }>

  /** How one match is going, and the answer once there is one. Never a cost. */
  matchProgress(matchId: string): Promise<MatchProgress>

  /**
   * Saves the pasted job description as an ordinary draft role.
   *
   * Only where a full job description was pasted. A bare title would make a
   * role with nothing in it, and a question is not a role at all.
   */
  saveMatchAsDraft(chatId: string): Promise<{ id: string; title: string; status: string }>

  /**
   * Asks a match to stop.
   *
   * A stopped match keeps nothing: no turn is added to the conversation, so
   * it reads as though the question was never asked. The agent call already
   * in flight is not recalled, because nothing can recall it.
   */
  stopMatch(matchId: string): Promise<{ state: MatchState }>

  /**
   * Rebuilds a band from the cuts a person ticked, and how much each counts.
   *
   * `emphasis` is a multiplier per code, defaulting to 1. It scales the
   * incumbent weighting rather than replacing it, so leaning on one source
   * still respects how many people stand behind the rest.
   */
  saveBenchmarkCuts(jdId: string, codes: string[], emphasis?: Record<string, number>, name?: string): Promise<Benchmark>

  /* ---------- salary ranges ---------- */

  /**
   * Every role, with the range this company has for it.
   *
   * One request serves both tabs. The roles the second tab shows are the ones
   * carrying `waiting`, so switching tabs asks nothing and the count is right
   * before anybody has looked at it.
   */
  salaryRanges(opts?: { page?: number; q?: string; family?: string; waiting?: boolean }): Promise<SalaryRanges>

  /**
   * One role's range, and the survey job its midpoint came from.
   *
   * Says whether a median is waiting but offers no way to accept it: that
   * decision belongs on the Suggested changes tab and nowhere else.
   */
  salaryRange(jdId: string): Promise<RangeDetail>

  /** The company's spread. Needs no approval and moves every row at once. */
  setCompanySpread(spread: number): Promise<{ spread: number }>

  /** One role's own spread, which takes it off the company's. */
  setRoleSpread(jdId: string, spread: number): Promise<{ jdId: string; spread: number; overridden: boolean }>

  /** Puts a role back on the company's spread. */
  clearRoleSpread(jdId: string): Promise<{ jdId: string; spread: number; overridden: boolean }>

  /**
   * Where one role's range sits in the market.
   *
   * Needs no approval, exactly like the spread: it is this company's own
   * choice about where to pay, not the market's figure moving under them. The
   * figure at the new percentile is accepted as it is chosen, so nothing is
   * left waiting straight afterwards.
   */
  setPercentile(jdId: string, percentile: Pct): Promise<{
    jdId: string; percentile: Pct; minimum: number; midpoint: number; maximum: number; spread: number
  }>

  /**
   * Takes the median the market is now reporting as this role's own.
   *
   * Refused where nothing is waiting, rather than writing the same figure
   * again and reporting a change that did not happen.
   */
  acceptRange(jdId: string): Promise<{ jdId: string; minimum: number; midpoint: number; maximum: number; spread: number }>

  /** Every waiting role at once, and only the ones that were waiting. */
  acceptAllRanges(): Promise<{ accepted: number }>

  /**
   * Fixes the ranges as they stand as the company's published set.
   *
   * Publishes what is there, not what is waiting: a median nobody has accepted
   * is not published and neither is the range it would become.
   */
  publishRanges(): Promise<{
    publishedAt: string; publishedBy: string; count: number
    added: number; changed: number; removed: number
  }>

  /**
   * The set as it was published, or nothing.
   *
   * Fixed at the moment it was published: a median accepted afterwards, or a
   * spread widened afterwards, does not change what this returns.
   */
  publishedRanges(): Promise<PublishedSet>

  listSurveys(): Promise<MarketSurvey[]>
  getSurveyFilters(surveyId: string): Promise<SurveyFilter[]>
  /** Starts emptying a survey. Answers at once; a worker does the rest. */
  removeSurvey(surveyId: string, confirmProvider: string): Promise<{ records: number }>

  /**
   * Uploads a survey file.
   *
   * `surveyId` adds rows to a survey already held, in which case provider,
   * year and industry come from that survey and are not asked for again.
   *
   * The job that comes back carries `possibleDuplicates`: surveys she already
   * holds that this file might repeat. It is a warning and nothing is refused
   * on it.
   */
  startSurveyUpload(input: {
    file: File
    provider?: string
    year?: string
    industry?: string
    surveyId?: string
  }): Promise<UploadJob>
  /** Corrections to the column names. Nothing is stored until this is sent. */
  confirmColumns(id: string, columns: SurveyColumn[]): Promise<{ columns: SurveyColumn[] }>
  /**
   * Accepts the survey and hands the writing to a worker.
   *
   * `dropThin` leaves out the rows filled far more sparsely than the rest.
   * **Unset means they load**, because a provider withholding a small sample
   * is normal and she paid for those rows too.
   */
  commitSurvey(id: string, dropThin?: boolean): Promise<{ accepted: boolean; expected: number }>

  createChangeRequest(input: {
    jdId: string
    reason: string
    changes: Partial<Record<ProposableField, ChangeValue>>
    baseVersion: number
  }): Promise<ChangeRequest>
  listChangeRequests(): Promise<ChangeRequest[]>
  approveChangeRequest(id: string): Promise<void>
  rejectChangeRequest(id: string, note: string): Promise<void>

  /* ---- setting up companies. Super admin only, refused for anyone else. ---- */
  listCompanies(): Promise<AdminCompanySummary[]>
  getCompany(slug: string): Promise<AdminCompanyDetail>
  /**
   * A company, its sign-in domains and its first admin in one go. The password
   * comes back once and is never retrievable again.
   */
  createCompany(input: {
    name: string
    slug: string
    domains: string[]
    adminEmail: string
    adminName: string
  }): Promise<{ company: { slug: string; name: string }; person: AdminPerson; password?: string; emailed?: boolean }>
  addDomain(slug: string, domain: string): Promise<string[]>
  removeDomain(slug: string, domain: string): Promise<string[]>
  /** How a company's people log in: a single sign-on connection's name, or null for passwords (STE-1022). */
  setCompanySso(slug: string, connection: string | null): Promise<string | null>
  addPerson(slug: string, input: { email: string; name: string; role: GiveableRole }): Promise<NewLogin>
  setPersonRole(slug: string, userId: string, role: GiveableRole): Promise<AdminPerson>
  setPersonAccess(slug: string, userId: string, disabled: boolean): Promise<AdminPerson>
  /** The temporary password to pass on, or null when the person was emailed a link (Auth0). */
  resetPassword(slug: string, userId: string): Promise<string | null>
  /** Sets a password super admin typed twice. Nothing comes back, and nothing is emailed (STE-1044). */
  setPassword(slug: string, userId: string, password: string, confirm: string): Promise<void>

  /* ---------- Iconic, the AI agent (STE-989) ---------- */

  /** The person's own chats, newest first; `q` searches titles and questions. 404 when Iconic is off. */
  iconicChats(q?: string, before?: string): Promise<{ chats: IconicChat[]; more: string | null }>
  startIconicChat(): Promise<IconicChat>
  /** A chat and its latest turns; `before` reads older ones. */
  iconicChat(chatId: string, before?: number): Promise<IconicChatView>
  renameIconicChat(chatId: string, title: string): Promise<IconicChat>
  /** Deletes the chat and every turn. Nothing Iconic changed is undone. */
  deleteIconicChat(chatId: string): Promise<void>
  /** Writes the question and queues it. The answer turn is polled. */
  askIconic(chatId: string, ask: { text: string; screen?: string; record?: string }): Promise<{ turnId: string }>
  iconicTurn(turnId: string): Promise<IconicTurn>
  stopIconicTurn(turnId: string): Promise<IconicTurn>
  /** OK: the server runs the proposal it stored, as the person pressing it. */
  confirmIconicAction(turnId: string, actionId: string): Promise<IconicTurn>
  cancelIconicAction(turnId: string, actionId: string): Promise<IconicTurn>
}

/**
 * The short lived token lives here, in memory only, and never in storage.
 * A script injected into the page cannot read it from anywhere, and it dies
 * with the tab. The long lived token is an httpOnly cookie the browser sends
 * on its own and no script can reach.
 */
let accessToken: string | null = null
export const setAccessToken = (t: string | null) => {
  accessToken = t
}

async function send(path: string, init?: RequestInit, token: string | null = accessToken): Promise<Response> {
  return fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  })
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  const held = tokenSource()
  if (held) {
    // Signed in through Auth0 (STE-1012): the SDK holds the token and renews it.
    res = await sendWithToken((token) => send(path, init, token), held)
  } else {
    res = await send(path, init)

    // The token lasts fifteen minutes, so an expiry mid-session is normal rather
    // than exceptional. Swap it for a fresh one using the cookie and try again,
    // once, so the person never sees it happen.
    if (res.status === 401 && !path.startsWith('/auth/')) {
      const refreshed = await refreshSession()
      if (refreshed) res = await send(path, init)
    }
  }

  if (!res.ok) {
    // The API answers a cross-tenant request with 404, never 403, so a 404 here
    // means "no such thing for you" and is safe to show as a plain not-found.
    let message = `Request failed (${res.status})`
    try {
      const body = (await res.json()) as { message?: string }
      if (body?.message) message = body.message
    } catch {
      /* keep the default */
    }
    throw new ApiError(res.status, message)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

const qs = (params: Record<string, string | number | null | undefined>) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** Swaps the cookie for a fresh token. Returns null when there is no session. */
async function refreshSession(): Promise<Me | null> {
  try {
    const res = await fetch('/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
    })
    if (!res.ok) return null
    const session = (await res.json()) as { accessToken: string; me: Me }
    setAccessToken(session.accessToken)
    return session.me
  } catch {
    return null
  }
}

/**
 * Hands the long lived token over to be stored as a cookie no script can read,
 * including ours and including any injected by an attacker, and returns who
 * just signed in.
 *
 * Shared by an ordinary sign in and by finishing a first one, because the two
 * differ in how the token was obtained and in nothing after that.
 */
async function keepSession(tokens: { accessToken: string; refreshToken: string }): Promise<Me> {
  setAccessToken(tokens.accessToken)
  const stored = await request<{ me: Me }>('/auth/session', {
    method: 'POST',
    body: JSON.stringify({ refreshToken: tokens.refreshToken, accessToken: tokens.accessToken }),
  })
  return stored.me ?? (await request<Me>('/api/me'))
}

type PresignedJob = UploadJob & { uploadUrl?: string }

const presign = (
  kind: 'single' | 'bulk' | 'docset' | 'market',
  fileName: string,
  extra: Record<string, string | undefined> = {},
) =>
  request<PresignedJob>('/api/uploads/presign', {
    method: 'POST',
    body: JSON.stringify({ kind, fileName, ...extra }),
  })

/**
 * The upload, with the retry this one request never had.
 *
 * It goes straight to storage rather than through our API, so it missed the
 * retry and the error wording the rest of the client has. A dropped connection
 * reached the customer as the browser's own "Failed to fetch", and pressing
 * the button again worked, which made the feature look broken at random.
 *
 * `lib/upload.ts` has the rules.
 */
const put = (url: string | undefined, body: Blob | File) => putWithRetry(url, body)

export const httpApi: Api = {
  login: async (email, password) => {
    // Which company this address belongs to. Needed before the exchange can
    // start, because one directory serves every company.
    const { tenant } = await request<{ tenant: string }>('/auth/resolve', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }).catch(() => {
      throw new ApiError(401, 'That email address and password do not match.')
    })

    // The password goes to Cognito from here and never touches our servers.
    const out = await signInWithSrp(tenant, email, password)
    if (out.kind === 'newPasswordNeeded') return out
    return { kind: 'signedIn', me: await keepSession(out.tokens) }
  },
  finishNewPassword: async (challenge, password) => keepSession(await challenge.finish(password)),
  me: async () => {
    // Signed in through Auth0 (STE-1019): the SDK already holds the session.
    // The Cognito cookie below does not exist there, and asking for it first
    // turned every Auth0 sign-in into "could not be finished".
    if (tokenSource()) return request<Me>('/api/me')
    // On a fresh page load there is no token in memory, only the cookie, so
    // the session is rebuilt from that before anything else is asked for.
    if (!accessToken) {
      const me = await refreshSession()
      if (!me) throw new ApiError(401, 'Your session has ended. Sign in again.')
      return me
    }
    return request<Me>('/api/me')
  },
  logout: async () => {
    await request<void>('/auth/logout', { method: 'POST' })
    setAccessToken(null)
  },

  listJobDescriptions: ({ q, cursor, page, limit }) =>
    request<Page<JobDescription>>(`/api/job-descriptions${qs({ q, cursor, page, limit })}`),
  getJobDescription: (id) =>
    request<JobDescription>(`/api/job-descriptions/${encodeURIComponent(id)}`),
  getVersions: (id) =>
    request<VersionEntry[]>(`/api/job-descriptions/${encodeURIComponent(id)}/versions`),
  createJobDescription: (draft, status) =>
    request<JobDescription>('/api/job-descriptions', {
      method: 'POST',
      body: JSON.stringify({ ...draft, status }),
    }),
  updateJobDescription: (id, draft, status) =>
    request<JobDescription>(`/api/job-descriptions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...draft, status }),
    }),
  deleteJobDescription: (id) =>
    request<void>(`/api/job-descriptions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  exportCsv: (q) =>
    request<{ fileName: string; body: string }>(`/api/job-descriptions/export.csv${qs({ q })}`),
  exportDocument: (id) =>
    request<{ fileName: string; body: string }>(
      `/api/job-descriptions/${encodeURIComponent(id)}/document`,
    ),

  // Both of these ask for a one time link and then put the content straight
  // into storage. The file never passes through our own code, and it is the
  // arrival of the object that starts the work, so skipping the upload leaves
  // a job that sits there looking empty rather than failing.
  startExtraction: async (file, text) => {
    const job = await presign('single', file?.name ?? 'pasted.txt')
    await put(job.uploadUrl, file ?? new Blob([text], { type: 'text/plain' }))
    return job
  },
  startBulkCheck: async (text, fileName, sheet) => {
    const job = await presign('bulk', fileName || 'pasted-rows.csv')
    await put(job.uploadUrl, sheet ?? new Blob([text], { type: 'text/csv' }))
    return job
  },
  startDocsetCheck: async (file) => {
    const job = await presign('docset', file.name)
    await put(job.uploadUrl, file)
    return job
  },
  getUpload: (id) => request<UploadJob>(`/api/uploads/${encodeURIComponent(id)}`),
  setOnRows: (id, patch) =>
    request<UploadJob>(`/api/uploads/${encodeURIComponent(id)}/rows`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  editRow: (id, line, set, sections) =>
    request<UploadJob>(`/api/uploads/${encodeURIComponent(id)}/rows`, {
      method: 'PATCH',
      body: JSON.stringify(sections ? { line, set, sections } : { line, set }),
    }),
  commitUpload: (id) =>
    request<{ imported: number }>(`/api/uploads/${encodeURIComponent(id)}/commit`, {
      method: 'POST',
    }),

  listMarketData: ({ q, survey, clauses = [], cursor, limit, from, signal }) =>
    /*
     * Sent in a body rather than a query string.
     *
     * A column can offer a thousand values, and somebody who ticks them all
     * and unticks one has ticked nine hundred and ninety nine. That does not
     * fit in a URL, and a filter that quietly stops working past a certain
     * number of ticks is worse than one that was never offered.
     */
    request<MarketPage>('/api/market-data/search', {
      method: 'POST',
      body: JSON.stringify({
        ...(q ? { q } : {}),
        ...(survey ? { survey } : {}),
        ...(cursor ? { cursor } : {}),
        ...(limit ? { limit } : {}),
        ...(from ? { from } : {}),
        clauses: clauses.filter(
          (c) =>
            c.keys.length > 0 &&
            ((c.any?.length ?? 0) > 0 ||
              (c.none?.length ?? 0) > 0 ||
              (c.contains ?? '').trim() !== ''),
        ),
      }),
      signal,
    }),
  getColumnValues: (surveyId, columnKey) =>
    request<ColumnValues>(
      `/api/market-data/surveys/${encodeURIComponent(surveyId)}` +
      `/columns/${encodeURIComponent(columnKey)}/values`,
    ),
  getMarketRecord: (id) =>
    request<{ record: MarketRecord; survey: MarketSurvey }>(
      `/api/market-data/${encodeURIComponent(id)}`,
    ),
  updateMarketRecord: async (id, values) => {
    const out = await request<{ record: MarketRecord }>(
      `/api/market-data/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ values }) },
    )
    return out.record
  },
  deleteMarketRecord: (id) =>
    request<{ surveyRemoved: boolean }>(`/api/market-data/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  benchmarkOverview: (params) =>
    request<BenchmarkOverview>(`/api/benchmarks${qs({ ...(params ?? {}) })}`),

  benchmarkEstimate: (scope) =>
    request<{ scope: string; count: number; seconds: number }>(
      `/api/benchmarks/estimate?scope=${encodeURIComponent(scope)}`,
    ),

  startBenchmarkRun: (scope) =>
    request<{ runId: string; count: number; alreadyRunning?: boolean }>('/api/benchmarks/runs', {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),

  benchmarkOneRole: (jdId) =>
    request<{ runId: string; count: number; alreadyRunning?: boolean }>('/api/benchmarks/runs', {
      method: 'POST',
      body: JSON.stringify({ jdId }),
    }),

  benchmarkRun: (runId) => request<RunProgress>(`/api/benchmarks/runs/${encodeURIComponent(runId)}`),

  stopBenchmarkRun: (runId) =>
    request<{ state: RunState }>(`/api/benchmarks/runs/${encodeURIComponent(runId)}/stop`, { method: 'POST' }),

  getBenchmark: (jdId) => request<Benchmark>(`/api/benchmarks/${encodeURIComponent(jdId)}`),
  askFeedback: (jdId, ask) =>
    request<{ feedbackId: string }>(`/api/benchmarks/${encodeURIComponent(jdId)}/feedback`, {
      method: 'POST', body: JSON.stringify(ask),
    }),
  getFeedback: (jdId) => request<FeedbackView>(`/api/benchmarks/${encodeURIComponent(jdId)}/feedback`),
  keepFeedback: (jdId, feedbackId) =>
    request<Benchmark>(
      `/api/benchmarks/${encodeURIComponent(jdId)}/feedback/${encodeURIComponent(feedbackId)}/keep`,
      { method: 'POST' },
    ),
  discardFeedback: (jdId, feedbackId) =>
    request<{ kept: 'current' }>(
      `/api/benchmarks/${encodeURIComponent(jdId)}/feedback/${encodeURIComponent(feedbackId)}/discard`,
      { method: 'POST' },
    ),

  matchOverview: () => request<{ hasSurvey: boolean }>('/api/match-a-job'),

  startMatchChat: () =>
    request<{ chatId: string; turns: MatchTurn[] }>('/api/match-a-job/chats', { method: 'POST' }),

  matchChat: (chatId) =>
    request<MatchChat>(`/api/match-a-job/chats/${encodeURIComponent(chatId)}`),

  forgetMatchChat: async (chatId) => {
    await request<void>(`/api/match-a-job/chats/${encodeURIComponent(chatId)}`, { method: 'DELETE' })
  },

  sendToMatch: (chatId, text) =>
    request<{ matchId: string; chatId?: string; alreadyGoing?: boolean; message?: string }>(
      `/api/match-a-job/chats/${encodeURIComponent(chatId)}/messages`,
      { method: 'POST', body: JSON.stringify({ text }) },
    ),

  matchProgress: (matchId) =>
    request<MatchProgress>(`/api/match-a-job/matches/${encodeURIComponent(matchId)}`),

  saveMatchAsDraft: (chatId) =>
    request<{ id: string; title: string; status: string }>(
      `/api/match-a-job/chats/${encodeURIComponent(chatId)}/draft`,
      { method: 'POST' },
    ),

  stopMatch: (matchId) =>
    request<{ state: MatchState }>(
      `/api/match-a-job/matches/${encodeURIComponent(matchId)}/stop`,
      { method: 'POST' },
    ),

  saveBenchmarkCuts: (jdId, codes, emphasis, name) =>
    request<Benchmark>(`/api/benchmarks/${encodeURIComponent(jdId)}`, {
      method: 'POST',
      body: JSON.stringify({ codes, emphasis, name }),
    }),

  salaryRanges: (opts: { page?: number; q?: string; family?: string; waiting?: boolean } = {}) => {
    const p = new URLSearchParams()
    if (opts.page) p.set('page', String(opts.page))
    if (opts.q) p.set('q', opts.q)
    if (opts.family) p.set('family', opts.family)
    if (opts.waiting) p.set('waiting', 'true')
    const query = p.toString()
    return request<SalaryRanges>(`/api/salary-ranges${query ? `?${query}` : ''}`)
  },

  salaryRange: (jdId) =>
    request<RangeDetail>(`/api/salary-ranges/${encodeURIComponent(jdId)}`),

  setCompanySpread: (spread) =>
    request<{ spread: number }>('/api/salary-ranges/spread', {
      method: 'PUT',
      body: JSON.stringify({ spread }),
    }),

  setRoleSpread: (jdId, spread) =>
    request<{ jdId: string; spread: number; overridden: boolean }>(
      `/api/salary-ranges/${encodeURIComponent(jdId)}/spread`,
      { method: 'PUT', body: JSON.stringify({ spread }) },
    ),

  clearRoleSpread: (jdId) =>
    request<{ jdId: string; spread: number; overridden: boolean }>(
      `/api/salary-ranges/${encodeURIComponent(jdId)}/spread`,
      { method: 'DELETE' },
    ),

  setPercentile: (jdId, percentile) =>
    request<{
      jdId: string; percentile: Pct
      minimum: number; midpoint: number; maximum: number; spread: number
    }>(
      `/api/salary-ranges/${encodeURIComponent(jdId)}/percentile`,
      { method: 'PUT', body: JSON.stringify({ percentile }) },
    ),

  acceptRange: (jdId) =>
    request<{ jdId: string; minimum: number; midpoint: number; maximum: number; spread: number }>(
      `/api/salary-ranges/${encodeURIComponent(jdId)}/accept`,
      { method: 'POST' },
    ),

  acceptAllRanges: () =>
    request<{ accepted: number }>('/api/salary-ranges/accept-all', { method: 'POST' }),

  publishRanges: () =>
    request<{
      publishedAt: string; publishedBy: string; count: number
      added: number; changed: number; removed: number
    }>('/api/salary-ranges/publish', { method: 'POST' }),

  publishedRanges: () => request<PublishedSet>('/api/salary-ranges/published'),

  listSurveys: async () => {
    const out = await request<{ items: MarketSurvey[] }>('/api/market-data/surveys')
    return out.items
  },
  getSurveyFilters: async (surveyId) => {
    const out = await request<{ filters: SurveyFilter[] }>(
      `/api/market-data/surveys/${encodeURIComponent(surveyId)}/filters`,
    )
    return out.filters
  },
  removeSurvey: (surveyId, confirmProvider) =>
    request<{ records: number }>(
      `/api/market-data/surveys/${encodeURIComponent(surveyId)}`,
      { method: 'DELETE', body: JSON.stringify({ confirmProvider }) },
    ),

  startSurveyUpload: async ({ file, provider, year, industry, surveyId }) => {
    const job = await presign('market', file.name, { provider, year, industry, surveyId })
    await put(job.uploadUrl, file)
    return job
  },
  confirmColumns: (id, columns) =>
    request<{ columns: SurveyColumn[] }>(`/api/uploads/${encodeURIComponent(id)}/columns`, {
      method: 'PUT',
      body: JSON.stringify({ columns }),
    }),
  commitSurvey: (id, dropThin) =>
    request<{ accepted: boolean; expected: number }>(
      `/api/uploads/${encodeURIComponent(id)}/commit`,
      { method: 'POST', body: JSON.stringify({ dropThin: dropThin === true }) },
    ),

  createChangeRequest: (input) =>
    request<ChangeRequest>('/api/change-requests', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  listChangeRequests: () => request<ChangeRequest[]>('/api/change-requests'),
  approveChangeRequest: (id) =>
    request<void>(`/api/change-requests/${encodeURIComponent(id)}/approve`, { method: 'POST' }),
  rejectChangeRequest: (id, note) =>
    request<void>(`/api/change-requests/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  listCompanies: async () =>
    (await request<{ companies: AdminCompanySummary[] }>('/api/admin/companies')).companies,
  getCompany: (slug) => request<AdminCompanyDetail>(`/api/admin/companies/${encodeURIComponent(slug)}`),
  createCompany: (input) =>
    request('/api/admin/companies', { method: 'POST', body: JSON.stringify(input) }),
  addDomain: async (slug, domain) =>
    (await request<{ domains: string[] }>(`/api/admin/companies/${encodeURIComponent(slug)}/domains`, {
      method: 'POST',
      body: JSON.stringify({ domain }),
    })).domains,
  removeDomain: async (slug, domain) =>
    (await request<{ domains: string[] }>(
      `/api/admin/companies/${encodeURIComponent(slug)}/domains/${encodeURIComponent(domain)}`,
      { method: 'DELETE' },
    )).domains,
  setCompanySso: async (slug, connection) =>
    (await request<{ company: { ssoConnection?: string | null } }>(`/api/admin/companies/${encodeURIComponent(slug)}/sso`, {
      method: 'PUT',
      body: JSON.stringify({ connection }),
    })).company.ssoConnection ?? null,
  addPerson: (slug, input) =>
    request<NewLogin>(`/api/admin/companies/${encodeURIComponent(slug)}/people`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  setPersonRole: async (slug, userId, role) =>
    (await request<{ person: AdminPerson }>(
      `/api/admin/companies/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    )).person,
  setPersonAccess: async (slug, userId, disabled) =>
    (await request<{ person: AdminPerson }>(
      `/api/admin/companies/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ disabled }) },
    )).person,
  resetPassword: async (slug, userId) =>
    (await request<{ password?: string; emailed?: boolean }>(
      `/api/admin/companies/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}/password`,
      { method: 'POST' },
    )).password ?? null,
  setPassword: async (slug, userId, password, confirm) => {
    await request<{ set: true }>(
      `/api/admin/companies/${encodeURIComponent(slug)}/people/${encodeURIComponent(userId)}/password`,
      { method: 'PUT', body: JSON.stringify({ password, confirm }) },
    )
  },

  iconicChats: (q, before) => request(`/api/iconic/chats${qs({ q, before })}`),
  startIconicChat: () => request<IconicChat>('/api/iconic/chats', { method: 'POST' }),
  iconicChat: (chatId, before) =>
    request<IconicChatView>(`/api/iconic/chats/${encodeURIComponent(chatId)}${qs({ before })}`),
  renameIconicChat: (chatId, title) =>
    request<IconicChat>(`/api/iconic/chats/${encodeURIComponent(chatId)}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  deleteIconicChat: (chatId) =>
    request<void>(`/api/iconic/chats/${encodeURIComponent(chatId)}`, { method: 'DELETE' }),
  askIconic: (chatId, ask) =>
    request<{ turnId: string }>(`/api/iconic/chats/${encodeURIComponent(chatId)}/messages`, {
      method: 'POST', body: JSON.stringify(ask),
    }),
  iconicTurn: (turnId) => request<IconicTurn>(`/api/iconic/turns/${encodeURIComponent(turnId)}`),
  stopIconicTurn: (turnId) =>
    request<IconicTurn>(`/api/iconic/turns/${encodeURIComponent(turnId)}/stop`, { method: 'POST' }),
  confirmIconicAction: (turnId, actionId) =>
    request<IconicTurn>(
      `/api/iconic/turns/${encodeURIComponent(turnId)}/actions/${encodeURIComponent(actionId)}/confirm`,
      { method: 'POST' },
    ),
  cancelIconicAction: (turnId, actionId) =>
    request<IconicTurn>(
      `/api/iconic/turns/${encodeURIComponent(turnId)}/actions/${encodeURIComponent(actionId)}/cancel`,
      { method: 'POST' },
    ),
}
